<script lang="ts">
  import {STEP} from '@/types/common';
  import {onMount} from 'svelte';
  import {step} from '@/store';
  import {TopNav, Container, Icon} from '@components';
  import Swal from 'sweetalert2';
  import Editor from '@tinymce/tinymce-svelte';
  import {HelpInformationService} from '@/services';
  import appConfig from '@/config';
  import type {HelpInformation} from '@/types/settings';

  let topNav: TopNav;
  let isHighlighted: boolean = false;
  let title: string = '';
  let description: string = '';
  let resourceUrl: string = '';
  let divId: string = '';
  let howTo: string = '';
  let bestPractices: string = '';
  let disabled: boolean = false;
  let loading: boolean = false;
  let error: any = [];
  let helpInformation: any = [];
  let isUpdating: boolean = false;
  let activeId: number | undefined;
  let message: string = '';
  let conf = {
    toolbar: 'undo redo | bold italic underline strikethrough superscript subscript codeformat | fontsizes align',
    menubar: false
  };

  const tinyApiKey = appConfig.tinyMCEKey;

  onMount(async () => {
    getHelpData();
  });

  const getHelpData = async () => {
    helpInformation = await new HelpInformationService().getHelpInformation();
  };

  const setValues = async (id: number) => {
    await clearValues();
    await new HelpInformationService().getHelpInformationId(id).then(response => {
      isUpdating = true;
      activeId = response.id;
      isHighlighted = response.isHighlighted;
      title = response.title;
      description = response.description;
      divId = response.divId;
      resourceUrl = response.resourceUrl;
      howTo = response.howTo;
      bestPractices = response.bestPractices;
    });
  };

  const clearValues = async () => {
    isUpdating = false;
    activeId = undefined;
    title = '';
    description = '';
    divId = '';
    resourceUrl = '';
    howTo = '';
    bestPractices = '';
  };

  const saveChanges = async (e: Event) => {
    e.preventDefault();

    const payload = {
      title: title,
      isHighlighted: isHighlighted,
      description: description,
      howTo: howTo,
      bestPractices: bestPractices,
      divId: divId,
      resourceUrl: resourceUrl
    };

    if (isUpdating) {
      message = 'Help Information Updated';
      await new HelpInformationService().updateHelpInformation(activeId, payload);
    } else {
      message = 'Help Information Added';
      await new HelpInformationService().createHelpInformation(payload);
      clearValues();
    }

    getHelpData();

    Swal.fire({
      position: 'bottom-end',
      icon: 'success',
      title: message,
      showConfirmButton: false,
      timer: 1500,
      toast: true
    });
  };

  const updatePrevStep = () => step.update(STEP.CHOOSE_VIDEO);
</script>

<TopNav
  bind:this={topNav}
  {disabled}
  hasPrevious={true}
  label="Help Form Settings"
  on:next={saveChanges}
  on:prev={updatePrevStep}
/>

<Container className="flex">
  <div class="relative flex w-3/12 flex-col gap-6 overflow-y-hidden bg-gray-400 bg-opacity-10">
    <!-- List of Help Items Here -->
    {#if helpInformation.length >= 1}
      <ul class="list-none">
        {#each helpInformation as help}
          <!-- svelte-ignore a11y-click-events-have-key-events -->
          <li class="cursor-pointer p-2" on:click={() => setValues(help.id)}>
            <div class="rounded-lg bg-white p-6">
              <h3 class="mb-2 text-lg font-bold">{help.title}</h3>
              <p class="text-sm leading-6 text-gray-600">{help.description}</p>
            </div>
          </li>
        {/each}
      </ul>
    {/if}
  </div>
  <div class="relative h-full w-9/12">
    <div class="container-div">
      <div
        class="mx-auto flex h-full w-full flex-grow flex-col items-center justify-center bg-gray-100 px-40 py-20 align-middle"
      >
        <div class="flex flex-grow flex-col gap-2">
          <form on:submit={saveChanges} name="help-form" class="flex flex-col bg-white">
            <div class="flex w-full flex-col gap-4 space-y-10 px-5 py-10">
              <div class="grid grid-cols-2 gap-x-4">
                <div>
                  <div class="block flex-grow">
                    <label for="title" class="mb-5 font-semibold text-gray-700">Title</label>
                    <input
                      type="text"
                      name="title"
                      placeholder="Title"
                      class="w-full rounded-md border border-gray-100 px-4 py-2 text-sm focus-visible:outline-gray-300"
                      bind:value={title}
                    />
                  </div>
                  <div class="block flex-grow">
                    <label for="title" class="mb-5 font-semibold text-gray-700">Description</label>
                    <input
                      type="text"
                      name="description"
                      placeholder="Description"
                      class="w-full rounded-md border border-gray-100 px-4 py-2 text-sm focus-visible:outline-gray-300"
                      bind:value={description}
                    />
                  </div>
                  <div class="block flex-grow">
                    <label for="title" class="mb-5 font-semibold text-gray-700">How To Use</label>
                    <Editor apiKey={tinyApiKey} id="howTo" {conf} bind:value={howTo} />
                  </div>
                </div>
                <div>
                  <div class="block flex-grow">
                    <label for="title" class="mb-5 font-semibold text-gray-700">DIV ID</label>
                    <input
                      type="text"
                      name="divId"
                      placeholder="DIV ID"
                      class="w-full rounded-md border border-gray-100 px-4 py-2 text-sm focus-visible:outline-gray-300"
                      bind:value={divId}
                    />
                  </div>
                  <div class="block flex-grow">
                    <label for="title" class="mb-5 font-semibold text-gray-700">Resource URL</label>
                    <input
                      type="text"
                      name="resourceUrl"
                      placeholder="Resource Video URL"
                      class="w-full rounded-md border border-gray-100 px-4 py-2 text-sm focus-visible:outline-gray-300"
                      bind:value={resourceUrl}
                    />
                  </div>
                  <div class="block flex-grow">
                    <label for="title" class="mb-5 font-semibold text-gray-700">Best Practices</label>
                    <Editor apiKey={tinyApiKey} id="bestPractices" {conf} bind:value={bestPractices} />
                  </div>
                </div>
              </div>
              {#if error !== ''}
                <span class="text-xs text-red-600">{error}</span>
              {/if}
            </div>
            <div class="flex justify-end rounded-b-md bg-gray-50 px-4 py-3">
              <div class="mr-auto flex items-center gap-2">
                <label for="toggleFour" class="flex cursor-pointer select-none items-center text-dark dark:text-white">
                  <div class="relative">
                    <input bind:checked={isHighlighted} type="checkbox" id="toggleFour" class="peer sr-only" />
                    <div
                      class="box dark:bg-dark-2 block h-8 w-14 rounded-full bg-dark bg-gray-200 peer-checked:bg-primary"
                    ></div>
                    <div
                      class="dot dark:bg-dark-5 absolute left-1 top-1 flex !h-6 !w-6 items-center justify-center rounded-full bg-white transition peer-checked:translate-x-full peer-checked:dark:bg-white"
                    ></div>
                  </div>
                </label>
                <div class="flex items-center">
                  <span>Highlight</span>
                </div>
              </div>
              <button
                type="button"
                class="mr-5 flex cursor-pointer items-center gap-2 rounded-md bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-300 focus:outline-none"
                on:click={clearValues}
              >
                <div>New Entry</div>
              </button>
              <button
                type="submit"
                class={'flex cursor-pointer items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-primary/90 focus:outline-none' +
                  (loading ? 'pointer-events-none cursor-not-allowed' : '')}
              >
                {#if loading}
                  <Icon name="Loading" className="w-5 h-5 !text-white" />
                {/if}
                <div>Save</div>
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</Container>
